/**
 * @fileoverview
 * Helper functions for storing and retrieving data from browser history state
 */

/**
 * Safely call history.replaceState with the current URL.
 * Host pages often monkey-patch History; omitting the URL arg (or letting
 * their patches throw) must not abort Soft PIP / player teardown.
 */
const safeReplaceState = (state: object) => {
  try {
    window.history.replaceState(state, '', window.location.href)
  } catch {
    // Host analytics History patches (e.g. TikTok → Unichn) can throw.
  }
}

/**
 * Replace the current history state with the provided data
 * @param data - Object to replace history state with
 */
export const removeFromHistoryState = (key: string) => {
  if (typeof window === 'undefined' || !window.history) {
    return
  }

  const currentState = window.history.state || {}
  const { [key]: _, ...newState } = currentState

  safeReplaceState(newState)
}

/**
 * Helper function to store data in browser history state
 * @param data - Object to store in history state
 * @param url - Optional URL to replace (defaults to current location)
 */
export const storeInHistoryState = (data: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.history) {
    return
  }

  const currentState = window.history.state || {}
  const newState = {
    ...currentState,
    ...data,
  }

  safeReplaceState(newState)
}

/**
 * Helper function to retrieve data from browser history state
 * @param key - Key to retrieve from history state
 * @returns The value associated with the key, or undefined if not found
 */
export const getFromHistoryState = (key: string): any => {
  if (
    typeof window === 'undefined' ||
    !window.history ||
    !window.history.state
  ) {
    return undefined
  }

  return window.history.state[key]
}

/**
 * Helper function to retrieve data from a specific URL's history state
 * This is useful when you have a URL and want to check if it has associated history state
 * @param url - The URL to check for history state
 * @param key - Key to retrieve from history state
 * @returns The value associated with the key, or undefined if not found
 */
export const getFromUrlHistoryState = (url: string, key: string): any => {
  // Note: This function assumes the URL matches the current location
  // since we can only access the current history state
  if (url === location.href) {
    return getFromHistoryState(key)
  }

  return undefined
}
